/**
 * Журнал ошибок клиента → `public.log_client_events` → `ops.client_errors`
 * (экран /management/health, план — docs/APP_HEALTH_MONITORING_PLAN.md, этап 3).
 *
 * ЗАЧЕМ. До этого `error`/`unhandledrejection` глушились без следа, и случай
 * «у одного франчайзи не грузится» (Тюмень, 08.10.2026) увидеть было нечем.
 *
 * Что пишется: JS-ошибки, необработанные промисы, ненайденные чанки, сетевые
 * отказы и 5xx запросов к Supabase, запросы дольше 5 с. Без тел запросов и
 * ответов: путь RPC, миллисекунды, код, версия, устройство.
 *
 * Очередь живёт в localStorage, а не в памяти: если связь оборвалась совсем,
 * события уйдут при следующем запуске — тогда сбой виден хотя бы постфактум
 * (на экране «доставлено с опозданием»).
 *
 * ⚠️ Журнал никогда не ломает приложение: любые сбои здесь проглатываются.
 */
import { getSupabase, setSupabaseFetchObserver, type SupabaseFetchInfo } from '@/lib/supabase'
import { getAppVersion, getDeviceType, getSessionId, sanitizePathname } from '@/lib/appTracking'

export type ClientErrorKind =
  | 'js_error'
  | 'unhandled_rejection'
  | 'chunk_load'
  | 'network_error'
  | 'server_error'
  | 'slow_request'

interface ClientErrorEvent {
  kind: ClientErrorKind
  message?: string
  source?: string
  path: string
  http_status?: number
  error_code?: string
  duration_ms?: number
  occurrences: number
  ts: number
  session_id: string
  app_version: string
  device_type: string
  online: boolean
  connection?: string
}

const QUEUE_KEY = 'client_error_queue_v1'
const SESSION_COUNT_KEY = 'client_error_count'
const CHUNK_RELOAD_KEY = 'chunk_reload_at'
const LOG_RPC = 'log_client_events'
/** Совпадает с порогом сервера (get_ops_health_dashboard → thresholds.slow_request_ms). */
const SLOW_REQUEST_MS = 5000
const MAX_QUEUE = 50
/** Сервер принимает не больше 20 за вызов. */
const MAX_BATCH = 20
/** Предохранитель от петли ошибок: дальше в этой сессии не копим. */
const MAX_PER_SESSION = 100
const FLUSH_DELAY_MS = 3000
const RETRY_DELAY_MS = 30000

const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i
const IGNORED_MESSAGE_RE = /ResizeObserver loop/i

let installed = false
let queue: ClientErrorEvent[] = []
let flushing = false
let sendingDisabled = false
let flushTimer: ReturnType<typeof setTimeout> | null = null

function readQueue(): ClientErrorEvent[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.slice(-MAX_QUEUE) : []
  } catch {
    return []
  }
}

function persistQueue(): void {
  try {
    if (queue.length) localStorage.setItem(QUEUE_KEY, JSON.stringify(queue))
    else localStorage.removeItem(QUEUE_KEY)
  } catch {
    // переполненное или закрытое хранилище — живём с очередью в памяти
  }
}

function bumpSessionCount(): boolean {
  try {
    const count = Number(sessionStorage.getItem(SESSION_COUNT_KEY) ?? '0') + 1
    sessionStorage.setItem(SESSION_COUNT_KEY, String(count))
    return count <= MAX_PER_SESSION
  } catch {
    return true
  }
}

function currentPath(): string {
  try {
    return sanitizePathname(window.location.pathname)
  } catch {
    return ''
  }
}

function connectionType(): string | undefined {
  const connection = (navigator as unknown as { connection?: { effectiveType?: string } }).connection
  return connection?.effectiveType
}

function urlPath(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname
  } catch {
    return url.split('?')[0] ?? ''
  }
}

function errorMessage(reason: unknown): string {
  if (reason instanceof Error) return reason.message
  if (reason && typeof reason === 'object' && 'message' in reason) {
    return String((reason as { message?: unknown }).message ?? '')
  }
  try {
    return typeof reason === 'string' ? reason : JSON.stringify(reason)
  } catch {
    return String(reason)
  }
}

function scheduleFlush(delay = FLUSH_DELAY_MS): void {
  if (flushTimer) return
  flushTimer = setTimeout(() => {
    flushTimer = null
    void flushClientErrors()
  }, delay)
}

export function reportClientError(
  event: Pick<ClientErrorEvent, 'kind'> &
    Partial<Pick<ClientErrorEvent, 'message' | 'source' | 'http_status' | 'error_code' | 'duration_ms'>>
): void {
  try {
    const message = event.message?.slice(0, 500)
    if (message && IGNORED_MESSAGE_RE.test(message)) return

    // Повтор той же ошибки до отправки — счётчик, а не новая строка.
    const same = queue.find(
      (item) => item.kind === event.kind && item.source === event.source && item.message === message
    )
    if (same) {
      same.occurrences += 1
      persistQueue()
      return
    }
    if (!bumpSessionCount()) return

    queue.push({
      kind: event.kind,
      message,
      source: event.source?.slice(0, 200),
      path: currentPath(),
      http_status: event.http_status,
      error_code: event.error_code,
      duration_ms: event.duration_ms === undefined ? undefined : Math.round(event.duration_ms),
      occurrences: 1,
      ts: Date.now(),
      session_id: getSessionId(),
      app_version: getAppVersion(),
      device_type: getDeviceType(),
      online: navigator.onLine,
      connection: connectionType(),
    })
    if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE)
    persistQueue()
    scheduleFlush()
  } catch {
    // never throw
  }
}

export async function flushClientErrors(): Promise<void> {
  if (flushing || sendingDisabled || queue.length === 0) return
  if (!navigator.onLine) return
  flushing = true
  const batch = queue.slice(0, MAX_BATCH)
  try {
    const { error } = await (getSupabase() as unknown as {
      rpc: (fn: string, args: Record<string, unknown>) => PromiseLike<{ error: unknown }>
    }).rpc(LOG_RPC, { p_events: batch })
    if (error) {
      // Функции на сервере нет (фронт выкатили раньше миграции) — до конца сессии не долбим,
      // очередь остаётся в localStorage до следующего запуска.
      if ((error as { code?: string }).code === 'PGRST202') {
        sendingDisabled = true
        return
      }
      scheduleFlush(RETRY_DELAY_MS)
      return
    }
    queue = queue.filter((item) => !batch.includes(item))
    persistQueue()
    if (queue.length) scheduleFlush(0)
  } catch {
    scheduleFlush(RETRY_DELAY_MS)
  } finally {
    flushing = false
  }
}

function observeSupabaseFetch(info: SupabaseFetchInfo): void {
  if (info.url.includes(`/rpc/${LOG_RPC}`)) return
  const source = urlPath(info.url)

  if (info.error) {
    // Ушли со страницы / отменили запрос — это не сбой.
    if ((info.error as { name?: string }).name === 'AbortError') return
    reportClientError({
      kind: 'network_error',
      message: errorMessage(info.error),
      source,
      duration_ms: info.durationMs,
    })
    return
  }

  if (info.status !== null && info.status >= 500) {
    const report = (code?: string) =>
      reportClientError({
        kind: 'server_error',
        source,
        http_status: info.status ?? undefined,
        error_code: code,
        duration_ms: info.durationMs,
      })
    // Код PostgREST (57014 = statement_timeout) — только поле code, без текста ответа.
    const response = info.response
    if (!response) {
      report()
      return
    }
    response
      .clone()
      .json()
      .then((body: unknown) => {
        const code = body && typeof body === 'object' ? (body as { code?: unknown }).code : undefined
        report(typeof code === 'string' ? code.slice(0, 16) : undefined)
      })
      .catch(() => report())
    return
  }

  // В свёрнутом приложении таймеры и сеть притормаживаются — длительность там не показатель.
  if (info.durationMs >= SLOW_REQUEST_MS && document.visibilityState === 'visible') {
    reportClientError({
      kind: 'slow_request',
      source,
      http_status: info.status ?? undefined,
      duration_ms: info.durationMs,
    })
  }
}

function sourceOf(filename: string | undefined, line?: number, column?: number): string | undefined {
  if (!filename) return undefined
  const path = urlPath(filename)
  return line ? `${path}:${line}:${column ?? 0}` : path
}

/** Один автоматический перезапуск на ненайденный чанк (старый index после выката). */
function reloadOnceForChunk(): void {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? '0')
    if (Date.now() - last < 60_000) return
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
    window.location.reload()
  } catch {
    // без sessionStorage не перезагружаем: иначе можно уйти в петлю
  }
}

export function installClientErrorReporter(): void {
  if (installed || typeof window === 'undefined') return
  installed = true
  queue = readQueue()

  setSupabaseFetchObserver(observeSupabaseFetch)

  // capture — чтобы поймать и ошибки загрузки <script>/<link>, они не всплывают.
  window.addEventListener(
    'error',
    (event: Event) => {
      const target = event.target as (HTMLElement & { src?: string; href?: string }) | null
      if (target instanceof HTMLElement) {
        const url = target.src || target.href
        if (!url) return
        reportClientError({
          kind: urlPath(url).includes('/assets/') ? 'chunk_load' : 'js_error',
          message: `не загрузился ${target.tagName.toLowerCase()}`,
          source: urlPath(url),
        })
        return
      }
      const errorEvent = event as ErrorEvent
      const message = errorEvent.message || errorMessage(errorEvent.error)
      reportClientError({
        kind: CHUNK_ERROR_RE.test(message) ? 'chunk_load' : 'js_error',
        message,
        source: sourceOf(errorEvent.filename, errorEvent.lineno, errorEvent.colno),
      })
    },
    true
  )

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const message = errorMessage(event.reason)
    reportClientError({
      kind: CHUNK_ERROR_RE.test(message) ? 'chunk_load' : 'unhandled_rejection',
      message,
    })
  })

  window.addEventListener('vite:preloadError', (event: Event) => {
    const payload = (event as Event & { payload?: unknown }).payload
    reportClientError({ kind: 'chunk_load', message: errorMessage(payload) })
    // Событие уже лежит в localStorage — перезапуск его не потеряет.
    event.preventDefault()
    reloadOnceForChunk()
  })

  window.addEventListener('online', () => scheduleFlush(0))
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flushClientErrors()
  })

  // Хвост прошлого запуска (связь оборвалась, приложение закрыли) — после старта.
  if (queue.length) scheduleFlush(5000)
}
